import { get } from 'svelte/store';
import type { DashboardRecord } from 'src/components/Dashboards/dash-types';
import { addDashboard, type DashboardVisual } from 'src/components/Lists/list-helpers';
import { postReq } from 'src/services/http';
import { dashboards } from 'src/stores/features/dashboards';
import {
	ENERGY_DASHBOARD_ID,
	ENERGY_DASHBOARD_ROUTE,
	ENERGY_V2_SCHEMA_VERSION,
	buildEnergyV2CloneDashboard,
	buildLegacyCustomEnergyV2Migration,
	ENERGY_V2_PURPOSE_TEMPLATES,
	cloneEnergyV2Widgets,
	createEnergyV2Config,
	normalizeEnergyV2Config,
	restoreActiveEnergyV2Layout,
	isEnergyPurposeId,
	type EnergyV2Config,
	type EnergyV2Widget,
	type EnergyPurposeId,
} from './energy-v2-types';

const persistDashboard = async (dashboard: DashboardRecord): Promise<boolean> => {
	const response = await postReq('/interface/dash/update', {
		id: dashboard.id,
		data: JSON.stringify(dashboard),
	});
	return Boolean(response?.isok);
};

export const buildEnergyV2Dashboard = (): DashboardRecord => {
	const energyV2 = createEnergyV2Config();
	return {
		id: ENERGY_DASHBOARD_ID,
		name: 'Energy',
		text: 'Energy',
		position: 1,
		route: ENERGY_DASHBOARD_ROUTE,
		kind: 'energy',
		protected: true,
		schemaVersion: 2,
		icon: 'fas fa-bolt',
		backgroundColor: 'color-0',
		devices: [],
		rooms: [],
		groups: [],
		scenes: [],
		alarms: [],
		temp_schemes: [],
		image: '',
		weather: {},
		dashWidgets: cloneEnergyV2Widgets(energyV2.purposes[energyV2.activePurpose].widgets),
		energyV2,
	};
};

export const ensureEnergyV2Dashboard = async (): Promise<DashboardRecord> => {
	const all = get(dashboards);
	const existing = all[ENERGY_DASHBOARD_ID] as DashboardRecord | undefined;
	const existingEnergyVersion = Number(existing?.energyV2?.schemaVersion);
	const hasCurrentV2 = existingEnergyVersion === ENERGY_V2_SCHEMA_VERSION;
	const hasMigratableV2 =
		hasCurrentV2 || (existingEnergyVersion >= 4 && existingEnergyVersion < ENERGY_V2_SCHEMA_VERSION);
	const energyV2 = normalizeEnergyV2Config(
		existing?.energyV2,
		hasMigratableV2 ? existing?.dashWidgets : undefined,
	);
	const normalized: DashboardRecord = {
		...buildEnergyV2Dashboard(),
		...existing,
		id: ENERGY_DASHBOARD_ID,
		kind: 'energy',
		protected: true,
		schemaVersion: 2,
		dashWidgets: cloneEnergyV2Widgets(energyV2.purposes[energyV2.activePurpose].widgets),
		energyV2,
	};
	delete normalized.energyV3;

	const changed =
		!existing ||
		existing.kind !== 'energy' ||
		existing.protected !== true ||
		existing.schemaVersion !== 2 ||
		JSON.stringify(existing.energyV2) !== JSON.stringify(normalized.energyV2) ||
		JSON.stringify(existing.dashWidgets) !== JSON.stringify(normalized.dashWidgets) ||
		'energyV3' in existing;

	if (changed) {
		// Opening the protected Energy dashboard is a read operation. Keep schema
		// normalization local until an explicit edit, restore, or purpose change is saved.
		dashboards.update({ ...all, [ENERGY_DASHBOARD_ID]: normalized });
	}

	return normalized;
};

const saveEnergyV2Dashboard = async (
	current: DashboardRecord,
	config: EnergyV2Config,
	widgets: EnergyV2Widget[],
): Promise<boolean> => {
	const all = get(dashboards);
	const updated: DashboardRecord = {
		...current,
		kind: 'energy',
		protected: true,
		schemaVersion: 2,
		energyV2: config,
		dashWidgets: cloneEnergyV2Widgets(widgets),
	};
	delete updated.energyV3;

	dashboards.update({ ...all, [current.id]: updated });
	const saved = await persistDashboard(updated);
	if (!saved) dashboards.update({ ...get(dashboards), [current.id]: current });
	return saved;
};

export const switchEnergyV2Purpose = async (
	dashboardId: string | number,
	purpose: EnergyPurposeId,
): Promise<boolean> => {
	const current = get(dashboards)[dashboardId] as DashboardRecord | undefined;
	if (!current) return false;

	const config = normalizeEnergyV2Config(current.energyV2, current.dashWidgets);
	if (purpose === config.activePurpose) return true;

	const nextConfig: EnergyV2Config = {
		...config,
		activePurpose: purpose,
		purposes: {
			...config.purposes,
			[config.activePurpose]: {
				widgets: cloneEnergyV2Widgets((current.dashWidgets ?? []) as EnergyV2Widget[]),
			},
		},
	};
	return saveEnergyV2Dashboard(current, nextConfig, nextConfig.purposes[purpose].widgets);
};

export const restoreActiveEnergyV2Purpose = async (dashboardId: string | number): Promise<boolean> => {
	const current = get(dashboards)[dashboardId] as DashboardRecord | undefined;
	if (!current) return false;

	const config = normalizeEnergyV2Config(current.energyV2, current.dashWidgets);
	const nextConfig = restoreActiveEnergyV2Layout(config);
	const widgets = nextConfig.purposes[config.activePurpose].widgets;
	return saveEnergyV2Dashboard(current, nextConfig, widgets);
};

export const updateEnergyV2DashboardVisual = async (
	dashboardId: string | number,
	visual: { icon?: string; backgroundColor?: string },
): Promise<boolean> => {
	const all = get(dashboards);
	const current = all[dashboardId] as DashboardRecord | undefined;
	if (!current) return false;

	const updated: DashboardRecord = {
		...current,
		icon: visual.icon?.trim() || current.icon,
		backgroundColor: visual.backgroundColor?.trim() || current.backgroundColor,
	};
	delete updated.image;

	dashboards.update({ ...all, [dashboardId]: updated });
	const saved = await persistDashboard(updated);
	if (!saved) dashboards.update({ ...get(dashboards), [dashboardId]: current });
	return saved;
};

export const cloneEnergyV2Dashboard = async (
	sourceDashboardId: string | number,
	name: string,
	visual?: DashboardVisual,
): Promise<string | number | undefined> => {
	const source = get(dashboards)[sourceDashboardId] as DashboardRecord | undefined;
	if (!source) return undefined;

	const newDashboardId = await addDashboard(name, visual);
	if (newDashboardId === undefined) return undefined;

	const created = get(dashboards)[newDashboardId] as DashboardRecord | undefined;
	if (!created) return undefined;

	const cloned = buildEnergyV2CloneDashboard(created, (source.dashWidgets ?? []) as EnergyV2Widget[]);

	const saved = await persistDashboard(cloned);
	if (!saved) return undefined;
	dashboards.update({ ...get(dashboards), [newDashboardId]: cloned });
	return newDashboardId;
};

export const initializeCustomEnergyDashboard = async (
	dashboardId: string | number,
	purpose: EnergyPurposeId,
): Promise<boolean> => {
	const all = get(dashboards);
	const current = all[dashboardId] as DashboardRecord | undefined;
	if (!current) return false;
	const initialized = buildEnergyV2CloneDashboard(
		current,
		ENERGY_V2_PURPOSE_TEMPLATES[purpose].widgets,
	);
	const saved = await persistDashboard(initialized);
	if (!saved) return false;
	dashboards.update({ ...get(dashboards), [dashboardId]: initialized });
	return true;
};

export const migrateLegacyCustomEnergyDashboard = async (
	dashboardId: string | number,
): Promise<boolean> => {
	const all = get(dashboards);
	const current = all[dashboardId] as DashboardRecord | undefined;
	if (!current || Number(dashboardId) === ENERGY_DASHBOARD_ID) return false;

	const legacyPurpose = current.energyV3?.activePurpose;
	const migrated = buildLegacyCustomEnergyV2Migration(
		current,
		isEnergyPurposeId(legacyPurpose) ? legacyPurpose : 'home',
	);
	const saved = await persistDashboard(migrated);
	if (!saved) return false;
	dashboards.update({ ...get(dashboards), [dashboardId]: migrated });
	return true;
};
